import type { Block, Document } from '@gitbook/slate';
import { getEmojiForCode } from '@gitbook/util-emojis';
import { List } from '@gitbook/util-immutable';

import * as expandable from '../expandable';
import {
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    INLINE_EMOJI,
    INLINE_LINK,
} from '../types';
import { getNodeTextForID, getSectionIDs, type SectionIDs } from './getSectionIDs';
import {
    isSectionDelimiter,
    isSectionDelimiterParent,
    type SectionDelimiterBlock,
    type SectionDelimiterType,
} from './isSectionDelimiter';

/**
 * A section represents a chunk of the document that is addressable by a URL.
 * It used to help the user better navigate the document through the page outline,
 * but also for content indexing where we want to index more semantic chunks.
 */
export interface DocumentSection {
    /**
     * The section delimiter block.
     * It can be null if the section is empty or if the section is not a delimiter block.
     */
    node: SectionDelimiterBlock | null;

    /** Type */
    type: SectionDelimiterType | null;

    /**
     * Type of the parent of this section. If not `document`, then this section is a nested
     * section and should be handled as such. Will be null when returning an empty section with
     * a null type.
     */
    parentType: string | null;

    /**
     * Key of the starting block in the document.
     * It can be used as an unique identifier for the section.
     */
    key: string;

    /**
     * ID in the url for this section.
     * It can be an empty string for the first section of the document.
     */
    anchor: string;

    /**
     * Plain text title of the section.
     */
    title: string;

    /**
     * Nodes contained in the section.
     * It represents the body of the section, and depending on the block, it can include or not
     * the delimiter block.
     */
    nodes: List<Block>;
}

/**
 * Cache to optimize multiple calls to getSectionsInDoc when rendering the application.
 */
const sectionInDocsCache = new WeakMap<Document, DocumentSection[]>();

/**
 * List all sections in a document.
 */
export function getSectionsInDoc(
    document: Document,
    sectionIDs: SectionIDs = getSectionIDs(document)
): DocumentSection[] {
    const inCache = sectionInDocsCache.get(document);
    if (inCache) {
        return inCache;
    }

    const sections = getSectionsInNodes(document.nodes, sectionIDs, document.object);
    sectionInDocsCache.set(document, sections);
    return sections;
}

function getSectionsInNodes(
    nodes: List<Block>,
    sectionIDs: SectionIDs,
    parentType: string
): DocumentSection[] {
    if (nodes.isEmpty()) {
        return [];
    }

    const firstNode = nodes.first() as Block;
    const result: DocumentSection[] = [];

    if ([BLOCK_COLUMNS, BLOCK_TABS].includes(firstNode.type)) {
        // For columns and tabs, we should look through all the block node and then get those nodes indexed
        // as sections.
        firstNode.blockNodes.forEach((child) => {
            if (
                (firstNode.type === BLOCK_TABS && child.type !== BLOCK_TABS_ITEM) ||
                (firstNode.type === BLOCK_COLUMNS && child.type !== BLOCK_COLUMN)
            ) {
                return;
            }

            const childNodes = child.nodes as List<Block>;
            const nextDelimiterIndex = childNodes.findIndex(isSectionDelimiter);
            const endSectionIndex = nextDelimiterIndex >= 0 ? nextDelimiterIndex : childNodes.size;

            result.push(
                createSectionForNode(child as SectionDelimiterBlock, sectionIDs, {
                    parentType,
                    // For columns and tabs, we want to make sure we add the content of each column/tab item up to the
                    // next section _within_ that column/tab item.
                    nodes: childNodes.slice(0, endSectionIndex),
                })
            );

            result.push(
                ...getSectionsInNodes(
                    childNodes.slice(endSectionIndex),
                    sectionIDs,
                    firstNode.type
                ).filter((block) => block.type)
            );
        });

        // Continue right after the tabs
        result.push(...getSectionsInNodes(nodes.rest(), sectionIDs, parentType));
    } else if (firstNode.type === BLOCK_EXPANDABLE) {
        result.push(
            createSectionForNode(firstNode as SectionDelimiterBlock, sectionIDs, {
                parentType,
                // For exandable, the entire content of the expandable is the body of the section.
                nodes: expandable.getBodyFragment(firstNode).nodes,
            })
        );

        // Continue right after the expandable
        result.push(...getSectionsInNodes(nodes.rest(), sectionIDs, parentType));
    } else if (firstNode.type === BLOCK_STEPPER) {
        firstNode.blockNodes.forEach((child) => {
            if (child.type !== BLOCK_STEPPER_STEP) {
                return;
            }

            const stepNodes = child.nodes as List<Block>;

            // We only add the first heading of stepper steps to the outline
            const stepHeadingNode = stepNodes.first();
            const stepHeadingBlockInList = stepNodes.slice(0, 1);

            if (
                stepHeadingNode &&
                ![BLOCK_HEADING_1, BLOCK_HEADING_2].includes(stepHeadingNode.type)
            ) {
                return;
            }

            result.push(
                createSectionForNode(child as SectionDelimiterBlock, sectionIDs, {
                    parentType,
                    // For stepper, only steps headings are section identifiers.
                    nodes: stepHeadingBlockInList,
                })
            );

            result.push(
                ...getSectionsInNodes(stepHeadingBlockInList, sectionIDs, firstNode.type).filter(
                    (block) => block.type
                )
            );
        });

        // Continue right after the stepper
        result.push(...getSectionsInNodes(nodes.rest(), sectionIDs, parentType));
    } else {
        const restNodes = nodes.rest();
        const nextDelimiterIndex = restNodes.findIndex(
            (node) => isSectionDelimiterParent(node) || isSectionDelimiter(node)
        );
        const endSectionIndex = nextDelimiterIndex >= 0 ? nextDelimiterIndex : restNodes.size;

        const nodesUntilNextSection = restNodes.slice(0, endSectionIndex);

        if (isSectionDelimiter(firstNode)) {
            result.push(
                createSectionForNode(firstNode, sectionIDs, {
                    parentType,
                    nodes: nodesUntilNextSection,
                })
            );
        } else {
            result.push({
                node: null,
                type: null,
                parentType,
                key: firstNode.key,
                title: '',
                anchor: '',
                nodes: nodesUntilNextSection.unshift(firstNode),
            });
        }

        if (nextDelimiterIndex >= 0) {
            result.push(
                ...getSectionsInNodes(restNodes.slice(nextDelimiterIndex), sectionIDs, parentType)
            );
        }
    }

    return result;
}

function createSectionForNode(
    node: SectionDelimiterBlock,
    sectionIDs: SectionIDs,
    partial: Partial<DocumentSection> = {}
): DocumentSection {
    return {
        node,
        type: node.type,
        parentType: 'document',
        key: node.key,
        title: getNodeTitle(node),
        anchor: sectionIDs.get(node.key) || '',
        nodes: List.create(),
        ...partial,
    };
}

function getNodeTitle(node: SectionDelimiterBlock): string {
    switch (node.type) {
        case BLOCK_OPENAPI:
        case BLOCK_OPENAPI_OPERATION:
        case BLOCK_OPENAPI_SCHEMAS:
        case BLOCK_OPENAPI_WEBHOOK:
        case BLOCK_TABS_ITEM:
        case BLOCK_EXPANDABLE:
            return getNodeTextForID(node);
        default: {
            return getNodeTitleWithEmoji(node);
        }
    }
}

function getNodeTitleWithEmoji(node: SectionDelimiterBlock): string {
    return node.nodes.reduce((acc, n) => {
        // If the title includes an emoji, parse it to include its string version
        if (n.type === INLINE_EMOJI && n.data.has('code')) {
            const emojiString = getEmojiForCode(n.data.get('code')) ?? '';
            return acc + emojiString;
        }
        // If the title includes a link, return its text content
        if (n.type === INLINE_LINK && n.text.length > 0) {
            return acc + n.text;
        }
        if (n.object === 'text' && n.text.length > 0) {
            return acc + n.text;
        }
        return acc;
    }, '');
}
